/**
 * Kaydedilmemiş değişiklik bekçisi (owner 2026-08-31, sheet 176).
 *
 * Bir ekranda kaydedilmemiş değişiklik varken sayfadan ayrılmanın DÖRT yolu
 * var ve dördü de ayrı yakalanmalı:
 *   1. Sekme kapatma / yenileme        -> `beforeunload` (tarayıcının kendi
 *                                         diyaloğu; burada kendi modalımızı
 *                                         gösteremeyiz, standart böyle)
 *   2. Tarayıcının geri tuşu           -> `popstate` (SPA'da beforeunload
 *                                         ÇALIŞMAZ: sayfa yüklemesi yok)
 *   3. Uygulama içi gezinme (sidebar)  -> `navigate` sarmalanır
 *   4. Aynı sayfada bölüm değişimi     -> ekran `requestLeave()` çağırır
 *
 * 2, 3 ve 4'te PROJENİN KENDİ MODALI çıkar (owner 2026-08-31: "bizim
 * modalımızla, default browser popup'ıyla olmayacak") ve üç seçenek sunar:
 * Kaydet ve çık · Kaydetmeden çık · Vazgeç.
 *
 * Kirli bayrak formda, gezinme üstte olduğu için kayıt burada merkezî
 * tutulur: form `useUnsavedChangesGuard` ile kaydolur (kaydetme fonksiyonunu
 * da verir), gezinme noktaları `requestLeave(devam)` çağırır.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from './TranslationContext';
import BaseModal from '../components/common/BaseModal';
import '../components/ConfirmationModal.scss';

export interface DirtyEntry {
  message: string;
  /** Varsa modaldaki "Kaydet ve çık" düğmesi görünür. */
  onSave?: () => Promise<boolean | void> | boolean | void;
  /** Discard / iptal durumunda çağrılır (örn. edit modundan çıkış, buffer temizleme). */
  onDiscard?: () => void;
}

interface UnsavedChangesValue {
  isDirty: () => boolean;
  /** Ayrılma isteği: temizse hemen `proceed()`, kirliyse modal açar. */
  requestLeave: (proceed: () => void) => void;
  /** Hard document leave (portal, logout). Same modal; proceed assigns location. */
  requestHardLeave: (href: string) => void;
  /** Form kaydı (hook kullanır; doğrudan çağırma). */
  register: (key: string, entry: DirtyEntry | null) => void;
}

const noop: UnsavedChangesValue = {
  isDirty: () => false,
  requestLeave: (proceed) => proceed(),
  requestHardLeave: (href) => {
    window.location.href = href;
  },
  register: () => {},
};

const Ctx = createContext<UnsavedChangesValue>(noop);

export const useUnsavedChanges = () => useContext(Ctx);

export const UnsavedChangesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const entries = useRef(new Map<string, DirtyEntry>());
  const [dirtyCount, setDirtyCount] = useState(0);
  const [pending, setPending] = useState<{ proceed: () => void } | null>(null);
  const [saving, setSaving] = useState(false);

  const register = useCallback((key: string, entry: DirtyEntry | null) => {
    const had = entries.current.has(key);
    if (entry) entries.current.set(key, entry);
    else entries.current.delete(key);
    if (had !== entries.current.has(key)) setDirtyCount(entries.current.size);
  }, []);

  const isDirty = useCallback(() => entries.current.size > 0, []);

  const clearAll = useCallback(() => {
    entries.current.clear();
    setDirtyCount(0);
  }, []);

  const requestLeave = useCallback((proceed: () => void) => {
    if (!entries.current.size) {
      proceed();
      return;
    }
    setPending({ proceed });
  }, []);

  const requestHardLeave = useCallback(
    (href: string) => {
      requestLeave(() => {
        window.location.href = href;
      });
    },
    [requestLeave]
  );

  // 1) Sekme kapatma / yenileme — tarayıcının kendi diyaloğu.
  useEffect(() => {
    if (!dirtyCount) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirtyCount]);

  // 2) Tarayıcının geri tuşu. Kirliyken bir "nöbetçi" geçmiş kaydı eklenir;
  //    geri basılınca onu tüketiriz, modalı açarız ve nöbetçiyi geri koyarız
  //    (sayfa yerinde kalsın). Kullanıcı çıkmayı seçerse gerçekten geri gideriz.
  useEffect(() => {
    if (!dirtyCount) return;
    let armed = true;
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      if (!armed) return;
      window.history.pushState(null, '', window.location.href);
      setPending({
        proceed: () => {
          armed = false;
          window.history.go(-2);
        },
      });
    };
    window.addEventListener('popstate', onPop);
    return () => {
      armed = false;
      window.removeEventListener('popstate', onPop);
    };
  }, [dirtyCount]);

  const finish = useCallback(
    (proceed: () => void) => {
      entries.current.forEach((entry) => {
        try {
          entry.onDiscard?.();
        } catch (err) {
          console.error('Error in onDiscard:', err);
        }
      });
      clearAll();
      setPending(null);
      proceed();
    },
    [clearAll]
  );

  const handleSaveAndLeave = useCallback(async () => {
    const entry = Array.from(entries.current.values())[0];
    const proceed = pending?.proceed;
    if (!entry?.onSave || !proceed) return;
    setSaving(true);
    try {
      const ok = await entry.onSave();
      if (ok === false) return; // kaydetme başarısız: modal açık kalsın
      finish(proceed);
    } finally {
      setSaving(false);
    }
  }, [pending, finish]);

  const value = useMemo(
    () => ({ isDirty, requestLeave, requestHardLeave, register }),
    // `dirtyCount` ŞART: yoksa context değeri değişmez ve `isDirty()`'ye
    // bakan tüketiciler kirli/temiz geçişinde yeniden render olmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDirty, requestLeave, requestHardLeave, register, dirtyCount]
  );

  const canSave = !!Array.from(entries.current.values())[0]?.onSave;
  const message =
    Array.from(entries.current.values())[0]?.message ||
    t('settings:common.unsaved_changes_warning');

  return (
    <Ctx.Provider value={value}>
      {children}
      <BaseModal
        isOpen={!!pending}
        onClose={() => setPending(null)}
        title={t('settings:common.unsaved_changes_title')}
        showCloseButton={false}
        maxWidth="440px"
        // Uyarı türü (`--warning`) KULLANILMAZ: ana düğmeyi turuncuya
        // boyuyordu; projede düğme renkleri tek (owner 2026-10-04).
        className="confirmation-modal"
        footer={
          <>
            <button
              type="button"
              className="cancel-button-v2"
              onClick={() => setPending(null)}
              disabled={saving}
            >
              {t('settings:common.unsaved_stay')}
            </button>
            <button
              type="button"
              className="cancel-button-v2"
              onClick={() => pending && finish(pending.proceed)}
              disabled={saving}
            >
              {t('settings:common.unsaved_discard')}
            </button>
            {canSave && (
              <button
                type="button"
                className="submit-button-v2"
                onClick={handleSaveAndLeave}
                disabled={saving}
              >
                {saving
                  ? t('settings:common.unsaved_saving')
                  : t('settings:common.unsaved_save')}
              </button>
            )}
          </>
        }
      >
        <div className="confirmation-modal-message">{message}</div>
      </BaseModal>
    </Ctx.Provider>
  );
};

export default UnsavedChangesProvider;
